"use client";

import type Hls from "hls.js";
import {
  type CSSProperties,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const DEFAULT_DURATION = 24 * 60 * 60;
const SEEK_EPSILON = 0.1;
const CONTROLS_IDLE_MS = 3_000;

type Chapter = {
  label: string;
  time: number;
  note: string;
};

type DemoRollout = {
  id: string;
  title: string;
  sourceLabel: string;
  description: string;
  streamUrl: string;
  poster: string;
  posterLabel: string;
  durationSeconds: number;
};

type ExplorerConfig = {
  streamUrl: string;
  poster: string;
  posterLabel: string;
  durationSeconds: number;
  hasAudio: boolean;
  links: {
    github: string;
    report: string;
    studio: string;
  };
  chapters: Chapter[];
  demos: DemoRollout[];
};

type QualityOption = {
  index: number;
  height: number;
  bitrate: number;
};

type PlayerState =
  | "idle"
  | "loading"
  | "ready"
  | "playing"
  | "paused"
  | "buffering"
  | "seeking"
  | "ended"
  | "unsupported"
  | "unconfigured"
  | "error";

const DEFAULT_CONFIG: ExplorerConfig = {
  streamUrl: "",
  poster: "assets/hero-production.webp",
  posterLabel: "Frame from the 24-hour rollout · 07:42:18",
  durationSeconds: DEFAULT_DURATION,
  hasAudio: false,
  links: {
    github: "https://github.com/amap-cvlab/ABot-World",
    report: "https://arxiv.org/abs/2607.19191",
    studio: "https://abot-world.amap.com",
  },
  chapters: [
    { label: "Opening", time: 0, note: "The continuous run begins" },
    { label: "Hour 06", time: 6 * 3600, note: "A quarter-day checkpoint" },
    { label: "Halfway", time: 12 * 3600, note: "Twelve uninterrupted hours" },
    { label: "Hour 18", time: 18 * 3600, note: "The final quarter begins" },
    { label: "Final minutes", time: 23 * 3600 + 50 * 60, note: "Inspect the end of the run" },
  ],
  demos: [
    {
      id: "grassland",
      title: "Grassland traversal",
      sourceLabel: "Rollout 01 · Saierda Grassland",
      description: "Action-conditioned navigation across open terrain.",
      streamUrl: "",
      poster: "assets/demo-grassland.webp",
      posterLabel: "Frame from the rollout · 01:30:00",
      durationSeconds: 3 * 60 * 60,
    },
    {
      id: "memory-0043",
      title: "Memory landscape 0043",
      sourceLabel: "Rollout 02 · Landscape 0043",
      description: "A grounded field sequence for inspecting texture and motion stability.",
      streamUrl: "",
      poster: "assets/demo-memory-0043.webp",
      posterLabel: "Frame from the rollout · 00:30:00",
      durationSeconds: 3 * 60 * 60,
    },
    {
      id: "twilight-hunt",
      title: "Twilight city hunt",
      sourceLabel: "Rollout 03 · Third-person scene 028",
      description: "A low-light journey with distant silhouettes and shifting atmosphere.",
      streamUrl: "",
      poster: "assets/demo-twilight-hunt.webp",
      posterLabel: "Frame from the rollout · 00:30:00",
      durationSeconds: 3 * 60 * 60,
    },
    {
      id: "memory-0055",
      title: "Aurora snowfield 0055",
      sourceLabel: "Rollout 04 · Landscape 0055",
      description: "A continuous journey across a high-contrast polar environment.",
      streamUrl: "",
      poster: "assets/demo-memory-0055.webp",
      posterLabel: "Frame from the rollout · 01:30:00",
      durationSeconds: 3 * 60 * 60,
    },
    {
      id: "physics-0010",
      title: "Desert physics 0010",
      sourceLabel: "Rollout 05 · Physics landscape 0010",
      description: "Continuous desert movement for examining subject and terrain coherence.",
      streamUrl: "",
      poster: "assets/demo-physics-0010.webp",
      posterLabel: "Frame from the rollout · 01:30:00",
      durationSeconds: 3 * 60 * 60,
    },
  ],
};

function clampTime(value: number, duration: number) {
  const finiteDuration = Number.isFinite(duration) && duration > 0
    ? duration
    : DEFAULT_DURATION;
  return Math.min(Math.max(value, 0), Math.max(finiteDuration - SEEK_EPSILON, 0));
}

function formatTimecode(value: number) {
  const seconds = Math.max(0, Math.floor(value));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder]
    .map((part) => String(part).padStart(2, "0"))
    .join(":");
}

function parseTimecode(raw: string) {
  const value = raw.trim();
  if (!value) return null;
  if (/^\d+(?:\.\d+)?$/.test(value)) return Number(value);

  const pieces = value.split(":");
  if (pieces.length < 2 || pieces.length > 3) return null;
  if (pieces.some((piece) => !/^\d{1,3}$/.test(piece))) return null;

  const numbers = pieces.map(Number);
  const [hours, minutes, seconds] = pieces.length === 3
    ? numbers
    : [0, numbers[0], numbers[1]];

  if (minutes > 59 || seconds > 59) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

function accessibleTime(value: number) {
  const seconds = Math.max(0, Math.floor(value));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return `${hours} hours ${minutes} minutes ${remainder} seconds`;
}

function resolveUrl(value: string) {
  if (!value || typeof document === "undefined") return value;
  return new URL(value, document.baseURI).toString();
}

function streamStatusLabel(state: PlayerState) {
  switch (state) {
    case "loading":
      return "Preparing the 24-hour stream";
    case "buffering":
      return "Buffering nearby segments";
    case "seeking":
      return "Loading the selected moment";
    case "playing":
      return "Playing continuous rollout";
    case "paused":
      return "Playback paused";
    case "ended":
      return "End of the 24-hour rollout";
    case "unsupported":
      return "This browser cannot play HLS video";
    case "unconfigured":
      return "Media URL is not configured yet";
    case "error":
      return "Playback could not be recovered";
    default:
      return "Full 24-hour rollout";
  }
}

function DemoCard({ demo, index }: { demo: DemoRollout; index: number }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "playing" | "error">("idle");
  const configured = Boolean(demo.streamUrl);

  const startPlayback = useCallback(async () => {
    const video = videoRef.current;
    const manifestUrl = resolveUrl(demo.streamUrl);
    if (!video || !manifestUrl) return;

    setState("loading");
    try {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = manifestUrl;
        video.load();
        await video.play();
        return;
      }

      const HlsModule = await import("hls.js");
      if (!HlsModule.default.isSupported()) throw new Error("HLS is unsupported");

      hlsRef.current?.destroy();
      const instance = new HlsModule.default({
        enableWorker: true,
        lowLatencyMode: false,
        capLevelToPlayerSize: true,
        backBufferLength: 20,
        maxBufferLength: 24,
      });
      hlsRef.current = instance;
      instance.on(HlsModule.Events.MEDIA_ATTACHED, () => instance.loadSource(manifestUrl));
      instance.on(HlsModule.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => setState("idle"));
      });
      instance.on(HlsModule.Events.ERROR, (_event, data) => {
        if (!data.fatal) return;
        setState("error");
        instance.destroy();
        hlsRef.current = null;
      });
      instance.attachMedia(video);
    } catch {
      setState("error");
    }
  }, [demo.streamUrl]);

  useEffect(() => () => hlsRef.current?.destroy(), []);

  const actionLabel = !configured
    ? "Transcoding in progress"
    : state === "loading"
      ? "Opening stream…"
      : state === "error"
        ? "Retry demo"
        : "Watch demo";

  return (
    <article className="demo-card">
      <div className="demo-media">
        <video
          ref={videoRef}
          poster={demo.poster}
          preload="none"
          playsInline
          crossOrigin="anonymous"
          controls={state === "playing"}
          aria-label={`${demo.title}, complete rollout`}
          onPlaying={() => setState("playing")}
          onEnded={() => setState("idle")}
          onError={() => setState("error")}
        />
        {state !== "playing" && (
          <button
            className="demo-play"
            type="button"
            disabled={!configured || state === "loading"}
            onClick={() => void startPlayback()}
            aria-label={configured ? `Play ${demo.title}` : `${demo.title} is being transcoded`}
          >
            <span className="demo-play-icon" aria-hidden="true">
              {configured ? "▶" : "·"}
            </span>
            <span>{actionLabel}</span>
          </button>
        )}
      </div>
      <div className="demo-copy">
        <div className="demo-kicker">
          <span>{String(index + 1).padStart(2, "0")}</span>
          <span>{demo.sourceLabel}</span>
        </div>
        <h3>{demo.title}</h3>
        <p>{demo.description}</p>
        <small>{demo.posterLabel}</small>
      </div>
    </article>
  );
}

export default function PlayerExperience() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerShellRef = useRef<HTMLDivElement | null>(null);
  const controlDeckRef = useRef<HTMLDivElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const attachPromiseRef = useRef<Promise<void> | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const pendingAutoplayRef = useRef(false);
  const networkRetriesRef = useRef(0);
  const mediaRetriesRef = useRef(0);
  const bufferingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controlsHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [playerState, setPlayerState] = useState<PlayerState>("idle");
  const [statusDetail, setStatusDetail] = useState("");
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [currentTime, setCurrentTime] = useState(0);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [jumpValue, setJumpValue] = useState("00:00:00");
  const [jumpError, setJumpError] = useState("");
  const [qualities, setQualities] = useState<QualityOption[]>([]);
  const [quality, setQuality] = useState(-1);
  const [nativeHls, setNativeHls] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [muted, setMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  const displayedTime = scrubTime ?? currentTime;
  const progress = Math.min(Math.max((displayedTime / duration) * 100, 0), 100);

  const clearControlsHideTimer = useCallback(() => {
    if (!controlsHideTimerRef.current) return;
    clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = null;
  }, []);

  const scheduleControlsHide = useCallback(() => {
    clearControlsHideTimer();
    controlsHideTimerRef.current = setTimeout(() => {
      if (controlDeckRef.current?.querySelector(":focus-visible")) return;
      setControlsVisible(false);
      controlsHideTimerRef.current = null;
    }, CONTROLS_IDLE_MS);
  }, [clearControlsHideTimer]);

  const revealControls = useCallback(() => {
    setControlsVisible(true);
    scheduleControlsHide();
  }, [scheduleControlsHide]);

  const updateDeepLink = useCallback((seconds: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set("t", formatTimecode(seconds));
    window.history.replaceState({}, "", url);
  }, []);

  useEffect(() => {
    const deepLink = new URLSearchParams(window.location.search).get("t");
    const parsed = deepLink ? parseTimecode(deepLink) : null;
    if (parsed !== null) {
      const target = clampTime(parsed, DEFAULT_DURATION);
      pendingSeekRef.current = target;
      window.queueMicrotask(() => {
        setCurrentTime(target);
        setJumpValue(formatTimecode(target));
      });
    }

    const configUrl = new URL("config.json", document.baseURI);
    fetch(configUrl, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`Config returned ${response.status}`);
        return response.json() as Promise<Partial<ExplorerConfig>>;
      })
      .then((incoming) => {
        const merged: ExplorerConfig = {
          ...DEFAULT_CONFIG,
          ...incoming,
          links: { ...DEFAULT_CONFIG.links, ...(incoming.links ?? {}) },
          chapters:
            Array.isArray(incoming.chapters) && incoming.chapters.length
              ? incoming.chapters
              : DEFAULT_CONFIG.chapters,
          demos:
            Array.isArray(incoming.demos) && incoming.demos.length
              ? incoming.demos
              : DEFAULT_CONFIG.demos,
        };
        const nextDuration = Number(merged.durationSeconds) || DEFAULT_DURATION;
        setConfig(merged);
        setHasAudio(Boolean(merged.hasAudio));
        setDuration(nextDuration);
        setCurrentTime((previous) => clampTime(previous, nextDuration));
      })
      .catch(() => {
        setStatusDetail("Using the bundled display configuration.");
      })
      .finally(() => setConfigLoaded(true));
  }, []);

  const applyPendingPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const target = pendingSeekRef.current;
    if (target !== null && Number.isFinite(video.duration)) {
      video.currentTime = clampTime(target, video.duration);
      pendingSeekRef.current = null;
    }
    if (pendingAutoplayRef.current) {
      pendingAutoplayRef.current = false;
      video.play().catch(() => {
        setPlayerState("paused");
        setStatusDetail("Press play to continue.");
      });
    }
  }, []);

  const attachPlayer = useCallback(
    (startAt: number, autoplay: boolean) => {
      pendingSeekRef.current = clampTime(startAt, duration);
      pendingAutoplayRef.current ||= autoplay;

      if (attachPromiseRef.current) return attachPromiseRef.current;

      attachPromiseRef.current = (async () => {
        const video = videoRef.current;
        const manifestUrl = resolveUrl(config.streamUrl);
        if (!video || !manifestUrl) {
          setPlayerState("unconfigured");
          setStatusDetail(
            "Set streamUrl in public/config.json after uploading the HLS package.",
          );
          return;
        }

        setPlayerState("loading");
        setStatusDetail(`Opening ${formatTimecode(startAt)}…`);

        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          setNativeHls(true);
          video.src = manifestUrl;
          video.load();
          return;
        }

        const HlsModule = await import("hls.js");
        const HlsConstructor = HlsModule.default;
        if (!HlsConstructor.isSupported()) {
          setPlayerState("unsupported");
          setStatusDetail("Try a current version of Safari, Chrome, Edge, or Firefox.");
          return;
        }

        const instance = new HlsConstructor({
          autoStartLoad: false,
          startPosition: clampTime(startAt, duration),
          enableWorker: true,
          lowLatencyMode: false,
          capLevelToPlayerSize: true,
          backBufferLength: 30,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
        });
        hlsRef.current = instance;

        instance.on(HlsModule.Events.MEDIA_ATTACHED, () => {
          instance.loadSource(manifestUrl);
        });
        instance.on(HlsModule.Events.MANIFEST_PARSED, () => {
          const available = instance.levels
            .map((level, index) => ({
              index,
              height: level.height,
              bitrate: level.bitrate,
            }))
            .filter((level) => level.height > 0)
            .sort((a, b) => b.height - a.height);
          setQualities(available);
          instance.startLoad(clampTime(startAt, duration));
          setPlayerState("ready");
          setStatusDetail("Choose any moment in the full 24-hour record.");
        });
        instance.on(HlsModule.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;
          if (
            data.type === HlsModule.ErrorTypes.NETWORK_ERROR &&
            networkRetriesRef.current < 2
          ) {
            networkRetriesRef.current += 1;
            setStatusDetail("Network interrupted. Retrying nearby segments…");
            window.setTimeout(
              () => instance.startLoad(video.currentTime || startAt),
              500 * 2 ** networkRetriesRef.current,
            );
            return;
          }
          if (
            data.type === HlsModule.ErrorTypes.MEDIA_ERROR &&
            mediaRetriesRef.current < 2
          ) {
            mediaRetriesRef.current += 1;
            setStatusDetail("Recovering the video decoder…");
            instance.recoverMediaError();
            return;
          }
          setPlayerState("error");
          setStatusDetail(
            "Check the media URL, CORS policy, and CDN object availability, then retry.",
          );
          instance.destroy();
          hlsRef.current = null;
          attachPromiseRef.current = null;
        });
        instance.attachMedia(video);
      })();

      return attachPromiseRef.current;
    },
    [config.streamUrl, duration],
  );

  const seekTo = useCallback(
    (rawTarget: number, autoplay = false, writeUrl = true) => {
      const target = clampTime(rawTarget, duration);
      const video = videoRef.current;
      setCurrentTime(target);
      setJumpValue(formatTimecode(target));
      setJumpError("");
      setPlayerState("seeking");
      setStatusDetail(`Loading the segment around ${formatTimecode(target)}…`);
      if (writeUrl) updateDeepLink(target);

      if (!attachPromiseRef.current) {
        void attachPlayer(target, autoplay);
      } else if (video && Number.isFinite(video.duration)) {
        video.currentTime = clampTime(target, video.duration);
        if (autoplay) void video.play();
      } else {
        pendingSeekRef.current = target;
        pendingAutoplayRef.current ||= autoplay;
      }
    },
    [attachPlayer, duration, updateDeepLink],
  );

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!attachPromiseRef.current) {
      void attachPlayer(currentTime, true);
      return;
    }
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => setStatusDetail("Press play to continue."));
    } else {
      video.pause();
    }
  }, [attachPlayer, currentTime]);

  const retryPlayback = useCallback(() => {
    hlsRef.current?.destroy();
    hlsRef.current = null;
    attachPromiseRef.current = null;
    networkRetriesRef.current = 0;
    mediaRetriesRef.current = 0;
    void attachPlayer(currentTime, true);
  }, [attachPlayer, currentTime]);

  const submitJump = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseTimecode(jumpValue);
    if (parsed === null || parsed < 0 || parsed > duration) {
      setJumpError("Use HH:MM:SS between 00:00:00 and 24:00:00.");
      return;
    }
    seekTo(parsed, false);
  };

  const copyMoment = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set("t", formatTimecode(currentTime));
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setStatusDetail("Copy the current URL to share this moment.");
    }
  }, [currentTime]);

  const toggleFullscreen = useCallback(async () => {
    const shell = playerShellRef.current;
    const video = videoRef.current;
    if (!shell || !video) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (typeof shell.requestFullscreen === "function") {
        await shell.requestFullscreen();
      } else {
        const webkitVideo = video as HTMLVideoElement & {
          webkitEnterFullscreen?: () => void;
        };
        if (typeof webkitVideo.webkitEnterFullscreen === "function") {
          webkitVideo.webkitEnterFullscreen();
        } else {
          setStatusDetail("Fullscreen is not available in this browser.");
        }
      }
    } catch {
      setStatusDetail("Fullscreen could not be opened. Use the browser video controls instead.");
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.matches("input, select, textarea, button, video") ||
        target?.isContentEditable
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === " " || key === "k") {
        event.preventDefault();
        togglePlayback();
      } else if (key === "arrowleft") {
        event.preventDefault();
        seekTo(currentTime - 5);
      } else if (key === "arrowright") {
        event.preventDefault();
        seekTo(currentTime + 5);
      } else if (key === "j") {
        event.preventDefault();
        seekTo(currentTime - 10);
      } else if (key === "l") {
        event.preventDefault();
        seekTo(currentTime + 10);
      } else if (key === "home") {
        event.preventDefault();
        seekTo(0);
      } else if (key === "end") {
        event.preventDefault();
        seekTo(duration);
      } else if (key === "f") {
        event.preventDefault();
        toggleFullscreen();
      } else if (key === "m" && hasAudio && videoRef.current) {
        event.preventDefault();
        videoRef.current.muted = !videoRef.current.muted;
        setMuted(videoRef.current.muted);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentTime, duration, hasAudio, seekTo, toggleFullscreen, togglePlayback]);

  useEffect(() => {
    const onActivity = () => revealControls();
    const onVisibilityChange = () => {
      if (!document.hidden) revealControls();
    };
    const page = document.documentElement;

    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("pointerdown", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    window.addEventListener("focus", onActivity);
    page.addEventListener("mouseleave", scheduleControlsHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
    scheduleControlsHide();

    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("pointerdown", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("focus", onActivity);
      page.removeEventListener("mouseleave", scheduleControlsHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearControlsHideTimer();
    };
  }, [clearControlsHideTimer, revealControls, scheduleControlsHide]);

  useEffect(() => {
    return () => {
      if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
      clearControlsHideTimer();
      hlsRef.current?.destroy();
    };
  }, [clearControlsHideTimer]);

  const rangeStyle = {
    "--timeline-progress": `${progress}%`,
  } as CSSProperties;

  const showRetry = playerState === "error";
  const configReady = configLoaded && Boolean(config.streamUrl);

  return (
    <main className="site-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="ABot-World-0 home">
          <span className="brand-wordmark">ABot-World-0</span>
          <span className="brand-orbit" aria-hidden="true" />
          <span className="brand-edition">24H ROLLOUT</span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#demos">More demos</a>
          <a href={config.links.studio} target="_blank" rel="noreferrer">
            Studio <span aria-hidden="true">↗</span>
          </a>
          <a href={config.links.github} target="_blank" rel="noreferrer">
            GitHub <span aria-hidden="true">↗</span>
          </a>
          <a href={config.links.report} target="_blank" rel="noreferrer">
            Technical report <span aria-hidden="true">↗</span>
          </a>
        </nav>
      </header>

      <section className="hero" id="top">
        <p className="eyebrow"><span /> Full-length inference record</p>
        <h1>24 hours of uninterrupted rollout.</h1>
        <p className="hero-copy">
          Navigate any moment across a complete day of continuous world-model
          inference, preserved as one seekable record.
        </p>
      </section>

      <section className="observatory" aria-labelledby="rollout-player-title">
        <h2 id="rollout-player-title" className="sr-only">
          ABot-World-0 24-hour rollout player
        </h2>
        <div
          className="player-frame"
          ref={playerShellRef}
          onMouseEnter={revealControls}
          onMouseMove={revealControls}
          onMouseLeave={scheduleControlsHide}
          onFocusCapture={revealControls}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              scheduleControlsHide();
            }
          }}
        >
          <div className="video-stage">
            <video
              ref={videoRef}
              poster={config.poster}
              preload="metadata"
              playsInline
              crossOrigin="anonymous"
              onClick={togglePlayback}
              onLoadedMetadata={(event) => {
                const video = event.currentTarget;
                if (Number.isFinite(video.duration) && video.duration > 0) {
                  setDuration(video.duration);
                }
                applyPendingPlayback();
              }}
              onDurationChange={(event) => {
                const nextDuration = event.currentTarget.duration;
                if (Number.isFinite(nextDuration) && nextDuration > 0) {
                  setDuration(nextDuration);
                }
              }}
              onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
              onPlaying={() => {
                if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                networkRetriesRef.current = 0;
                mediaRetriesRef.current = 0;
                setIsPlaying(true);
                setPlayerState("playing");
                setStatusDetail("Streaming only the segments around this moment.");
              }}
              onPause={() => {
                setIsPlaying(false);
                setPlayerState((previous) =>
                  previous === "ended" ? previous : "paused",
                );
              }}
              onWaiting={() => {
                if (bufferingTimerRef.current) clearTimeout(bufferingTimerRef.current);
                bufferingTimerRef.current = setTimeout(
                  () => setPlayerState("buffering"),
                  300,
                );
              }}
              onSeeked={() => {
                setPlayerState(videoRef.current?.paused ? "paused" : "playing");
                setStatusDetail(`Ready at ${formatTimecode(videoRef.current?.currentTime ?? 0)}.`);
              }}
              onEnded={() => {
                setIsPlaying(false);
                setPlayerState("ended");
              }}
              onVolumeChange={(event) => setMuted(event.currentTarget.muted)}
              onError={() => {
                if (nativeHls) {
                  setPlayerState("error");
                  setStatusDetail("The native HLS stream could not be loaded. Check its URL and CORS policy.");
                }
              }}
            />

            <div className="video-vignette" aria-hidden="true" />

            {!isPlaying && playerState !== "loading" && playerState !== "buffering" && (
              <button
                className="hero-play"
                type="button"
                onClick={togglePlayback}
                aria-label="Play the 24-hour rollout"
              >
                <span aria-hidden="true">▶</span>
              </button>
            )}

            {(playerState === "loading" ||
              playerState === "buffering" ||
              playerState === "seeking") && (
              <div className="loading-indicator" role="status">
                <span className="loading-ring" aria-hidden="true" />
                <span>{streamStatusLabel(playerState)}</span>
              </div>
            )}
          </div>

          <div
            ref={controlDeckRef}
            className={`control-deck${controlsVisible ? "" : " is-hidden"}`}
            aria-hidden={!controlsVisible}
          >
            <div className="timeline-labels" aria-hidden="true">
              {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((hour) => (
                <span key={hour}>{String(hour).padStart(2, "0")}</span>
              ))}
            </div>
            <div className="timeline-hitbox">
              <input
                className="timeline"
                type="range"
                min="0"
                max={duration}
                step="1"
                value={displayedTime}
                style={rangeStyle}
                aria-label="Seek through the 24-hour rollout"
                aria-valuemin={0}
                aria-valuemax={Math.floor(duration)}
                aria-valuenow={Math.floor(displayedTime)}
                aria-valuetext={accessibleTime(displayedTime)}
                onChange={(event) => setScrubTime(Number(event.target.value))}
                onPointerUp={(event) => {
                  seekTo(Number(event.currentTarget.value));
                  setScrubTime(null);
                }}
                onPointerCancel={(event) => {
                  seekTo(Number(event.currentTarget.value));
                  setScrubTime(null);
                }}
                onKeyUp={(event) => {
                  seekTo(Number(event.currentTarget.value));
                  setScrubTime(null);
                }}
                onBlur={(event) => {
                  if (scrubTime === null) return;
                  seekTo(Number(event.currentTarget.value));
                  setScrubTime(null);
                }}
              />
            </div>

            <div className="transport-row">
              <div className="transport-buttons">
                <button type="button" onClick={togglePlayback} aria-label={isPlaying ? "Pause" : "Play"}>
                  <span aria-hidden="true">{isPlaying ? "Ⅱ" : "▶"}</span>
                </button>
                <button type="button" onClick={() => seekTo(currentTime - 10)} aria-label="Back 10 seconds">
                  <span aria-hidden="true">−10</span>
                </button>
                <button type="button" onClick={() => seekTo(currentTime + 10)} aria-label="Forward 10 seconds">
                  <span aria-hidden="true">+10</span>
                </button>
                {hasAudio && (
                  <button
                    type="button"
                    onClick={() => {
                      const video = videoRef.current;
                      if (!video) return;
                      video.muted = !video.muted;
                    }}
                    aria-label={muted ? "Unmute" : "Mute"}
                  >
                    <span aria-hidden="true">{muted ? "MUTE" : "VOL"}</span>
                  </button>
                )}
              </div>

              <div className="time-readout" aria-live="off">
                {formatTimecode(displayedTime)}
                <span>/ {formatTimecode(duration)}</span>
              </div>

              <div className={`stream-state state-${playerState}`} aria-live="polite">
                <span aria-hidden="true" />
                {streamStatusLabel(playerState)}
              </div>

              <div className="utility-controls">
                <label className="quality-control">
                  <span>Quality</span>
                  <select
                    value={quality}
                    disabled={nativeHls || qualities.length === 0}
                    aria-label="Playback quality"
                    onChange={(event) => {
                      const next = Number(event.target.value);
                      setQuality(next);
                      if (hlsRef.current) hlsRef.current.currentLevel = next;
                      setStatusDetail(
                        next === -1
                          ? "Adaptive quality enabled."
                          : `Quality locked to ${hlsRef.current?.levels[next]?.height ?? "selected"}p.`,
                      );
                    }}
                  >
                    <option value={-1}>{nativeHls ? "Auto · Safari" : "Auto"}</option>
                    {qualities.map((option) => (
                      <option value={option.index} key={option.index}>
                        {option.height}p · {Math.round(option.bitrate / 1000)} kbps
                      </option>
                    ))}
                  </select>
                </label>
                <button className="copy-button" type="button" onClick={copyMoment}>
                  {copied ? "Copied" : "Copy moment"}
                </button>
                <button className="fullscreen-button" type="button" onClick={toggleFullscreen} aria-label="Enter fullscreen">
                  <span aria-hidden="true">⛶</span>
                </button>
              </div>
            </div>

            <form className="jump-form" onSubmit={submitJump}>
              <label htmlFor="jump-time">Jump to time</label>
              <input
                id="jump-time"
                value={jumpValue}
                inputMode="numeric"
                spellCheck={false}
                aria-describedby="jump-hint jump-error"
                aria-invalid={Boolean(jumpError)}
                onChange={(event) => setJumpValue(event.target.value)}
                placeholder="07:42:18"
              />
              <button type="submit">Go</button>
              <span id="jump-hint">HH:MM:SS</span>
              <span id="jump-error" className="jump-error" role="alert">{jumpError}</span>
            </form>

            <div className="player-message" aria-live="polite">
              <span>{statusDetail || (configReady ? "The stream starts only after you interact." : "Add the CDN master playlist URL to config.json before launch.")}</span>
              {showRetry && <button type="button" onClick={retryPlayback}>Retry playback</button>}
            </div>
          </div>
        </div>
      </section>

      <section className="metric-strip" aria-label="Rollout specifications">
        <article><strong>720P</strong><span>Resolution</span></article>
        <article><strong>≈16 FPS</strong><span>Real-time</span></article>
        <article><strong>24H+</strong><span>Recorded worlds</span></article>
        <article><strong>6</strong><span>Complete rollout samples</span></article>
      </section>

      <section className="section demo-section" id="demos">
        <div className="section-heading">
          <p className="section-index">01 / Extended rollouts</p>
          <h2>Five more worlds. Complete, uncut rollouts.</h2>
          <p>
            Different environments expose different failure modes. These full-length
            demos keep the quiet stretches, transitions, and difficult moments intact.
          </p>
        </div>
        <div className="demo-grid">
          {config.demos.map((demo, index) => (
            <DemoCard demo={demo} index={index} key={demo.id} />
          ))}
        </div>
      </section>

      <section className="section moment-section" id="moments">
        <div className="section-heading">
          <p className="section-index">02 / Navigate the evidence</p>
          <h2>One day, available at any second.</h2>
          <p>
            Jump directly into the continuous record. Each selection requests only
            the few media segments around that time—not the entire 100 GB source file.
          </p>
        </div>
        <div className="moment-grid">
          {config.chapters.map((chapter, index) => (
            <button
              type="button"
              className="moment-card"
              key={`${chapter.time}-${chapter.label}`}
              onClick={() => {
                seekTo(chapter.time);
                document.querySelector(".observatory")?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            >
              <span className="moment-number">{String(index + 1).padStart(2, "0")}</span>
              <strong>{formatTimecode(chapter.time)}</strong>
              <span>{chapter.label}</span>
              <small>{chapter.note}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="section proof-section">
        <div className="proof-lead">
          <p className="section-index">03 / What the record shows</p>
          <h2>Long-horizon generation, made inspectable.</h2>
        </div>
        <div className="proof-grid">
          <article>
            <span className="proof-icon">∞</span>
            <h3>Continuous evolution</h3>
            <p>
              A single long-form autoregressive rollout, exposed as a complete
              timeline rather than a short highlight reel.
            </p>
          </article>
          <article>
            <span className="proof-icon">16</span>
            <h3>Desktop real-time</h3>
            <p>
              The reported demo configuration reaches roughly 16 FPS at 720P on
              one RTX 5090, using about 19 GB of memory.
            </p>
          </article>
          <article>
            <span className="proof-icon">06</span>
            <h3>Six-second access</h3>
            <p>
              Aligned HLS segments and keyframes let the player open the requested
              moment without downloading the hours before it.
            </p>
          </article>
        </div>
      </section>

      <section className="section technical-section">
        <div>
          <p className="section-index">04 / ABot-World-0</p>
          <h2>Infinite interactive world rollout on a single desktop GPU.</h2>
        </div>
        <div className="technical-copy">
          <p>
            ABot-World-0 combines action-conditioned generation, long-horizon
            distribution matching, and an efficiency-oriented inference stack.
            Explore the model, report, and open-source release.
          </p>
          <div className="cta-row">
            <a href={config.links.github} target="_blank" rel="noreferrer">Open GitHub <span>↗</span></a>
            <a href={config.links.report} target="_blank" rel="noreferrer">Read the report <span>↗</span></a>
          </div>
        </div>
      </section>

      <footer>
        <span>ABot-World-0 · AMap CVLab</span>
        <span>One 24-hour flagship and five additional complete inference records.</span>
      </footer>
    </main>
  );
}
